<template>
  <div :style="themePageStyle">
    <!-- Header -->
    <div class="dev-topbar">
      <div class="dev-topbar-inner">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="dev-brand">
          <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:28px;"/>
          <span class="dev-brand-tag">Developers</span>
        </a>
        <div class="dev-nav">
          <a href="javascript:void(0)" @click="$router.push('/developers')" class="dev-nav-link">Apps</a>
          <a href="javascript:void(0)" @click="$router.push('/developers/docs')" class="dev-nav-link">Docs</a>
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="dev-nav-link" style="opacity:0.7;">← Back to Flockr</a>
        </div>
      </div>
    </div>

    <div class="container py-4" style="max-width:1000px;">
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

      <div v-else-if="!app" class="text-center py-5"><p :style="{color:'var(--fm-s,#6B6356)'}">App not found.</p></div>

      <div v-else>
        <a href="javascript:void(0)" @click="$router.push('/developers')" style="color:#A09888;text-decoration:none;font-size:12px;">
          <i class="mdi mdi-arrow-left"></i> All apps
        </a>

        <div class="d-flex align-items-start justify-content-between mt-2 mb-4">
          <div>
            <h2 class="fw-bold mb-1" :style="{color:'var(--fm-h,#F5F0E8)'}">{{ app.name }}</h2>
            <p class="mb-0" :style="{color:'var(--fm-s,#6B6356)',fontSize:13}">{{ app.description || 'No description' }}</p>
          </div>
          <button class="btn btn-sm btn-outline-danger" @click="confirmDelete">
            <i class="mdi mdi-delete-outline"></i>
          </button>
        </div>

        <!-- Newly created key reveal -->
        <div v-if="newlyCreatedKey" class="dev-key-reveal mb-4">
          <div class="d-flex align-items-start gap-3">
            <i class="mdi mdi-shield-key" style="font-size:24px;color:#F1B44C;"></i>
            <div style="flex:1;min-width:0;">
              <div class="fw-bold mb-1" style="color:#F5F0E8;font-size:14px;">Your new API key — copy it now</div>
              <div style="color:#D8CDB6;font-size:12.5px;margin-bottom:8px;">
                This is the only time you'll see the full key. Store it somewhere safe.
              </div>
              <div class="dev-secret-row">
                <code class="dev-secret">{{ newlyCreatedKey }}</code>
                <button class="btn btn-sm btn-warning" @click="copyText(newlyCreatedKey)">
                  <i class="mdi mdi-content-copy me-1"></i> Copy
                </button>
              </div>
            </div>
            <button class="dev-modal-close" @click="newlyCreatedKey=''"><i class="mdi mdi-close"></i></button>
          </div>
        </div>

        <!-- API Keys -->
        <div class="dev-section mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0" :style="{color:'var(--fm-h,#F5F0E8)'}">API Keys</h5>
            <button class="btn btn-sm btn-warning fw-bold" @click="generateKey">
              <i class="mdi mdi-plus me-1"></i> New Key
            </button>
          </div>
          <div v-if="!keys.length" :style="{color:'var(--fm-s,#6B6356)',fontSize:13}">No keys yet — create one to start hitting the API.</div>
          <table v-else class="dev-table">
            <thead><tr><th>Prefix</th><th>Env</th><th>Created</th><th>Last used</th><th>Status</th><th></th></tr></thead>
            <tbody>
              <tr v-for="k in keys" :key="k.id">
                <td><code style="color:#E5C77E;font-size:12px;">{{ k.key_prefix }}…</code></td>
                <td><span :class="'dev-env-pill dev-env-' + k.env">{{ k.env }}</span></td>
                <td>{{ fmtDate(k.created_at) }}</td>
                <td>{{ k.last_used_at ? fmtDate(k.last_used_at) : '—' }}</td>
                <td><span :class="'dev-status dev-status-' + k.status">{{ k.status }}</span></td>
                <td>
                  <button v-if="k.status==='active'" class="btn btn-sm btn-outline-danger" @click="revokeKey(k.id)">Revoke</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Webhook -->
        <div class="dev-section mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0" :style="{color:'var(--fm-h,#F5F0E8)'}">Webhook</h5>
            <span v-if="webhook" class="dev-pill dev-pill-hook">
              <i class="mdi mdi-circle" style="font-size:8px;color:#50C878;"></i> Active
            </span>
          </div>
          <p :style="{color:'var(--fm-s,#6B6356)',fontSize:13}">
            Get real-time POST notifications when events happen on Flockr.
            Each delivery is signed via HMAC-SHA256 in the <code>X-Flockr-Signature</code> header.
          </p>

          <label class="dev-label">Endpoint URL</label>
          <input v-model="webhookForm.url" class="form-control mb-2" placeholder="https://your-server.com/webhooks/flockr" :style="themeInputStyle"/>

          <label class="dev-label">Events</label>
          <div class="d-flex gap-2 flex-wrap mb-3">
            <label v-for="ev in eventOptions" :key="ev" class="dev-checkbox">
              <input type="checkbox" :value="ev" v-model="webhookForm.events"/>
              <span>{{ ev }}</span>
            </label>
          </div>

          <button class="btn btn-warning fw-bold" :disabled="!webhookForm.url || webhookForm.savingHook" @click="saveWebhook">
            <span v-if="!webhookForm.savingHook">{{ webhook ? 'Update Webhook' : 'Save Webhook' }}</span>
            <span v-else><i class="mdi mdi-loading mdi-spin"></i> Saving...</span>
          </button>

          <!-- Show secret if just created/loaded -->
          <div v-if="webhook && webhook.secret" class="dev-secret-row mt-3">
            <span style="color:#A09888;font-size:11px;">Signing secret:</span>
            <code class="dev-secret">{{ revealedSecret ? webhook.secret : '••••••••••••••••••••••••' }}</code>
            <button class="btn btn-sm btn-outline-secondary" @click="revealedSecret = !revealedSecret">
              <i :class="revealedSecret ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="copyText(webhook.secret)">
              <i class="mdi mdi-content-copy"></i>
            </button>
          </div>
        </div>

        <!-- ANITRAC Verifier — synchronous lookup URL DVS / DI registries plug in -->
        <div class="dev-section mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0" :style="{color:'var(--fm-h,#F5F0E8)'}">
              <i class="mdi mdi-shield-check me-1" style="color:#50C878;"></i> ANITRAC Verifier
            </h5>
            <span v-if="verifier" class="dev-pill dev-pill-hook">
              <i class="mdi mdi-circle" style="font-size:8px;color:#50C878;"></i> Active
            </span>
          </div>
          <p :style="{color:'var(--fm-s,#6B6356)',fontSize:13}">
            If your platform is the authoritative source for a livestock ID system (e.g. <strong>Kenya's ANITRAC central registry</strong>),
            register a lookup URL here. Every time a Flockr seller enters or updates an ANITRAC tag, we'll POST to your URL with the tag
            and expect a JSON <code>{{ '{ valid: true | false }' }}</code> reply within 5 s. Only when you reply <code>valid:true</code> does Flockr light up the
            green <strong>ANITRAC ✓</strong> trust chip on that animal's listings.
          </p>

          <label class="dev-label">Verifier URL (HTTPS)</label>
          <input v-model="verifierForm.url" class="form-control mb-2" placeholder="https://dvs.go.ke/anitrac/verify" :style="themeInputStyle"/>
          <div :style="{color:'var(--fm-s,#6B6356)',fontSize:11,marginBottom:8}">
            Request body: <code>{{ '{ "anitrac_id": "KE-CTL-…", "context": {…} }' }}</code> · Header <code>X-Flockr-Signature</code> = HMAC-SHA256(body, your signing secret) · Header <code>X-Flockr-Entity-Type: anitrac</code>
          </div>

          <button class="btn btn-warning fw-bold" :disabled="!verifierForm.url || verifierForm.saving" @click="saveVerifier">
            <span v-if="!verifierForm.saving">{{ verifier ? 'Update Verifier' : 'Save Verifier' }}</span>
            <span v-else><i class="mdi mdi-loading mdi-spin"></i> Saving...</span>
          </button>

          <div v-if="verifier && verifier.secret" class="dev-secret-row mt-3">
            <span style="color:#A09888;font-size:11px;">Verifier secret:</span>
            <code class="dev-secret">{{ revealedVerifierSecret ? verifier.secret : '••••••••••••••••••••••••' }}</code>
            <button class="btn btn-sm btn-outline-secondary" @click="revealedVerifierSecret = !revealedVerifierSecret">
              <i :class="revealedVerifierSecret ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="copyText(verifier.secret)">
              <i class="mdi mdi-content-copy"></i>
            </button>
          </div>

          <div v-if="verifier" class="mt-3" :style="{color:'var(--fm-s,#6B6356)',fontSize:11.5}">
            <strong>{{ verifier.total_verifications || 0 }}</strong> total checks  ·  <strong style="color:#50C878;">{{ verifier.total_valid || 0 }}</strong> returned valid  ·  last status: {{ verifier.last_status_code || '—' }}
          </div>
        </div>

        <!-- Recent deliveries -->
        <div class="dev-section">
          <h5 class="mb-3" :style="{color:'var(--fm-h,#F5F0E8)'}">Recent Deliveries</h5>
          <div v-if="!deliveries.length" :style="{color:'var(--fm-s,#6B6356)',fontSize:13}">
            No webhook deliveries yet. They'll appear here once your app starts receiving events.
          </div>
          <table v-else class="dev-table">
            <thead><tr><th>Event</th><th>Status</th><th>Code</th><th>Attempts</th><th>When</th></tr></thead>
            <tbody>
              <tr v-for="d in deliveries" :key="d.id">
                <td><code style="color:#E5C77E;font-size:11.5px;">{{ d.event_type }}</code></td>
                <td><span :class="'dev-status dev-status-' + d.status">{{ d.status }}</span></td>
                <td>{{ d.last_response_code || '—' }}</td>
                <td>{{ d.attempts }}</td>
                <td>{{ fmtDate(d.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { executeJson, flocker } from "@/api";
export default {
  name: "DeveloperAppDetail",
  data() { return {
    loading: true,
    app: null,
    keys: [],
    webhook: null,
    deliveries: [],
    newlyCreatedKey: '',
    revealedSecret: false,
    revealedVerifierSecret: false,
    webhookForm: { url: '', events: ['*'], savingHook: false },
    eventOptions: ['*', 'listing.*', 'sale.*', 'offtaker_order.*'],
    verifier: null,
    verifierForm: { url: '', saving: false },
    darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
  }},
  computed: {
    themePageStyle() { return this.darkMode ? 'background:#0A0A0A;min-height:100vh;color:#F5F0E8;' : 'background:#F7F5F2;min-height:100vh;color:#1A1A1A;' },
    themeInputStyle() { return this.darkMode ? 'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;' : 'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;' },
  },
  mounted() {
    const t = localStorage.getItem('flockr_theme') || 'dark'; window.__flockrSetTheme && window.__flockrSetTheme(t);
    const u = sessionStorage.getItem('userId');
    if (!u) { this.$router.push('/login?redirect=/developers'); return; }
    this.fetchApp();
  },
  methods: {
    async fetchApp() {
      this.loading = true;
      try {
        const r = await executeJson({
          function: 'getDeveloperApp',
          userId: sessionStorage.getItem('userId'),
          appId: this.$route.params.id,
        }, flocker);
        if (r.data.success) {
          this.app = r.data.data.app;
          this.keys = r.data.data.keys || [];
          const hooks = r.data.data.webhooks || [];
          this.webhook = hooks.length ? hooks[0] : null;
          if (this.webhook) {
            this.webhookForm.url = this.webhook.url;
            try { this.webhookForm.events = JSON.parse(this.webhook.events); } catch(e) { this.webhookForm.events = ['*']; }
          }
          this.deliveries = r.data.data.recent_deliveries || [];
          const verifiers = r.data.data.verifiers || [];
          this.verifier = verifiers.length ? verifiers[0] : null;
          if (this.verifier) {
            this.verifierForm.url = this.verifier.url;
          }
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    async saveVerifier() {
      if (!this.verifierForm.url.trim()) return;
      this.verifierForm.saving = true;
      try {
        const r = await executeJson({
          function: 'setDeveloperVerifier',
          userId: sessionStorage.getItem('userId'),
          appId: this.app.id,
          entityType: 'anitrac',
          url: this.verifierForm.url.trim(),
        }, flocker);
        if (r.data.success) {
          this.verifier = r.data.data;
          this.revealedVerifierSecret = true;
          await this.fetchApp();
        } else alert(r.data.message || 'Failed');
      } catch(e) { alert('Connection error'); }
      this.verifierForm.saving = false;
    },
    async generateKey() {
      try {
        const r = await executeJson({
          function: 'createDeveloperApiKey',
          userId: sessionStorage.getItem('userId'),
          appId: this.app.id,
          env: 'live',
        }, flocker);
        if (r.data.success) {
          this.newlyCreatedKey = r.data.data.plaintext;
          await this.fetchApp();
        } else alert(r.data.message || 'Failed');
      } catch(e) { alert('Connection error'); }
    },
    async revokeKey(keyId) {
      if (!confirm('Revoke this key? Any apps using it will stop working immediately.')) return;
      try {
        const r = await executeJson({
          function: 'revokeDeveloperApiKey',
          userId: sessionStorage.getItem('userId'),
          keyId,
        }, flocker);
        if (r.data.success) await this.fetchApp();
        else alert(r.data.message || 'Failed');
      } catch(e) { alert('Connection error'); }
    },
    async saveWebhook() {
      if (!this.webhookForm.url.trim()) return;
      this.webhookForm.savingHook = true;
      try {
        const r = await executeJson({
          function: 'setDeveloperWebhook',
          userId: sessionStorage.getItem('userId'),
          appId: this.app.id,
          url: this.webhookForm.url.trim(),
          events: JSON.stringify(this.webhookForm.events.length ? this.webhookForm.events : ['*']),
        }, flocker);
        if (r.data.success) {
          this.webhook = r.data.data;
          this.revealedSecret = true;     // newly issued secret — show once
          await this.fetchApp();
        } else alert(r.data.message || 'Failed');
      } catch(e) { alert('Connection error'); }
      this.webhookForm.savingHook = false;
    },
    async confirmDelete() {
      if (!confirm('Delete this app? All API keys will be revoked and webhook deliveries will stop.')) return;
      try {
        const r = await executeJson({
          function: 'deleteDeveloperApp',
          userId: sessionStorage.getItem('userId'),
          appId: this.app.id,
        }, flocker);
        if (r.data.success) this.$router.push('/developers');
        else alert(r.data.message || 'Failed');
      } catch(e) { alert('Connection error'); }
    },
    copyText(t) {
      navigator.clipboard.writeText(t).then(() => {
        const btn = event && event.target.closest('button');
        if (btn) { const o = btn.innerHTML; btn.innerHTML = '<i class="mdi mdi-check"></i> Copied'; setTimeout(() => btn.innerHTML = o, 1500); }
      });
    },
    fmtDate(s) { if (!s) return ''; const d = new Date(s.replace(' ', 'T')); return d.toLocaleString('en-GB', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}); },
  }
}
</script>

<style>
.dev-section { background: var(--fm-bg1, #141414); border: 1px solid var(--fm-br, #1E1E1E); border-radius: 12px; padding: 20px; }
.dev-key-reveal { background: linear-gradient(120deg,#3a2c10,#1f1808); border: 1px solid #F1B44C; border-radius: 12px; padding: 16px; }

.dev-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
.dev-table thead th { color: #A09888; font-weight: 700; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; padding: 6px 8px; border-bottom: 1px solid var(--fm-br, #1E1E1E); }
.dev-table tbody td { padding: 10px 8px; border-bottom: 1px solid var(--fm-br, #1E1E1E); color: var(--fm-h, #D8CDB6); }
.dev-table tbody tr:last-child td { border-bottom: none; }

.dev-env-pill { display: inline-block; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
.dev-env-live { background: rgba(80, 200, 120, 0.15); color: #50C878; }
.dev-env-test { background: rgba(241, 180, 76, 0.15); color: #F1B44C; }

.dev-status { display: inline-block; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; }
.dev-status-active, .dev-status-delivered { background: rgba(80, 200, 120, 0.15); color: #50C878; }
.dev-status-revoked, .dev-status-failed { background: rgba(229, 83, 60, 0.15); color: #E5533C; }
.dev-status-pending { background: rgba(241, 180, 76, 0.15); color: #F1B44C; }

.dev-secret-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.dev-secret { background: #0A0A0A; color: #E5C77E; padding: 8px 12px; border-radius: 6px; font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 12px; word-break: break-all; flex: 1; min-width: 0; }

.dev-checkbox { display: inline-flex; align-items: center; gap: 6px; background: var(--fm-bg1, #0F0F0F); border: 1px solid var(--fm-br, #2A2520); padding: 6px 12px; border-radius: 16px; font-size: 12.5px; color: var(--fm-h, #D8CDB6); cursor: pointer; }
.dev-checkbox input { margin: 0; }
</style>
